/**
 * QrJoinOverlay — overlay full-screen com o QR de entrada da aula.
 *
 * Renderizado só na view do mestre (GameMasterPanel), que é a tela projetada:
 *   - abre automaticamente quando a aula ainda está no slide 0 (antes de
 *     começar a apresentação);
 *   - reabre pelo botão "QR" do MasterControlsPill, para atrasados — quem
 *     escaneia entra direto no slide ativo (snapshot traz currentSlideIndex).
 *
 * Estado local do mestre: mostrar/ocultar NÃO propaga via WS — os alunos
 * continuam vendo o slide corrente nos celulares.
 *
 * QR via api.qrserver.com (sem lib local); a URL aparece em texto como
 * fallback. Esc ou o botão fecham.
 */

import { useEffect } from 'react'

import { useParticipants } from '../hooks/useParticipants'

interface Props {
  tableId: string
  onClose: () => void
}

export function QrJoinOverlay({ tableId, onClose }: Props) {
  const joinUrl = `${window.location.origin}/module_lab/join/${encodeURIComponent(tableId)}`
  const qrSrc =
    'https://api.qrserver.com/v1/create-qr-code/?size=560x560&margin=2&data=' +
    encodeURIComponent(joinUrl)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      role="dialog"
      aria-label="QR code de entrada na aula"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 90,
        display: 'grid',
        placeItems: 'center',
        background: 'var(--color-lab-bg, #FAFAF7)',
        fontFamily: 'var(--font-lab-sans, system-ui, sans-serif)',
        padding: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 20,
          textAlign: 'center',
        }}
      >
        <h1
          style={{
            fontSize: '1.8rem',
            fontWeight: 700,
            color: 'var(--color-lab-ink, #2B2F36)',
            margin: 0,
          }}
        >
          Entre na aula
        </h1>
        <img
          src={qrSrc}
          alt={`QR code para ${joinUrl}`}
          style={{
            width: 'min(56vh, 80vw)',
            height: 'auto',
            borderRadius: 12,
            background: '#FFFFFF',
            padding: 12,
            boxShadow: 'var(--lab-shadow, 0 4px 16px rgba(0,0,0,0.12))',
          }}
        />
        <span
          style={{
            fontFamily: 'var(--font-lab-mono, monospace)',
            fontSize: '1.1rem',
            color: 'var(--color-lab-accent, #5B2DB8)',
            wordBreak: 'break-all',
          }}
        >
          {joinUrl}
        </span>
        <PresentRoster />
        <button
          type="button"
          onClick={onClose}
          autoFocus
          style={{
            padding: '12px 24px',
            borderRadius: 8,
            border: 'none',
            background: 'var(--color-lab-accent, #5B2DB8)',
            color: '#FFFFFF',
            fontSize: '1rem',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Apresentar
        </button>
        <span style={{ fontSize: '0.75rem', color: 'var(--color-lab-ink-3, #555B66)' }}>
          Esc também fecha. Reabra pelo botão QR nos controles.
        </span>
      </div>
    </div>
  )
}

/**
 * Lista ao vivo de quem já entrou: snapshot traz os conectados, eventos
 * participantUpdate (joined/left) atualizam em tempo real enquanto o QR
 * está projetado. connected === false são memberships pré-populadas do
 * roster da mesa que (ainda) não conectaram — ficam de fora.
 */
function PresentRoster() {
  const players = useParticipants('players').filter((p) => p.connected !== false)

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        maxWidth: 'min(720px, 90vw)',
      }}
      aria-live="polite"
    >
      <span
        style={{
          fontSize: '0.85rem',
          fontWeight: 600,
          color: 'var(--color-lab-ink-3, #555B66)',
        }}
      >
        {players.length === 0
          ? 'Ninguém entrou ainda'
          : players.length === 1
            ? '1 pessoa na aula'
            : `${players.length} pessoas na aula`}
      </span>
      {players.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 6,
            maxHeight: '18vh',
            overflowY: 'auto',
          }}
        >
          {players.map((p) => (
            <span
              key={p.membershipId}
              style={{
                padding: '4px 12px',
                borderRadius: 999,
                background: '#FFFFFF',
                border: '1px solid var(--color-lab-rule, #D8D5CB)',
                color: 'var(--color-lab-ink, #2B2F36)',
                fontSize: '0.85rem',
                fontWeight: 600,
                boxShadow: 'var(--lab-shadow-sm, 0 1px 3px rgba(0,0,0,0.08))',
              }}
            >
              {p.displayName || 'Sem nome'}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
